import { extractApiError, type HttpApiClient } from "@vana/core";
import {
  type Ambassador,
  type DeliveryReport,
  type ReassignParams,
  type RegisteredAddress,
  type RescheduleParams,
  type SupervisorDelivery,
} from "../../domain/entities/supervisor-delivery";
import {
  GetDeliveryDetailError,
  ListAddressesError,
  ListAmbassadorsError,
  ReassignDeliveryError,
  RescheduleDeliveryError,
} from "../../domain/errors/errors";
import { type SupervisorDeliveryRepository } from "../../domain/repos/supervisor-delivery-repo";
import {
  type AddressesResponse,
  type AmbassadorsResponse,
  type DetailResponse,
  type ReportsResponse,
} from "../../infrastructure/responses/supervisor-delivery-responses";

export class SupervisorDeliveryRepo implements SupervisorDeliveryRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async getDetail(requestId: string): Promise<SupervisorDelivery> {
    try {
      const response = await this.apiClient.get<DetailResponse>(`v1/requests/${requestId}`);
      return response.data as SupervisorDelivery;
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new GetDeliveryDetailError(message, apiError);
    }
  }

  async listReports(requestId: string): Promise<DeliveryReport[]> {
    try {
      const response = await this.apiClient.get<ReportsResponse>(
        `v1/requests/${requestId}/problem-reports`,
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new GetDeliveryDetailError(message, apiError);
    }
  }

  async listAmbassadors(): Promise<Ambassador[]> {
    try {
      const response = await this.apiClient.get<AmbassadorsResponse>("v1/users?role=ambassador");
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListAmbassadorsError(message, apiError);
    }
  }

  async listAddresses(requestId: string): Promise<RegisteredAddress[]> {
    try {
      const response = await this.apiClient.get<AddressesResponse>(
        `v1/requests/${requestId}/addresses`,
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListAddressesError(message, apiError);
    }
  }

  async reassign(params: ReassignParams): Promise<void> {
    try {
      // The request interceptor snake_cases the body (newAmbassadorId ->
      // new_ambassador_id); the reassigning supervisor is the session user.
      await this.apiClient.post(`v1/requests/${params.requestId}/reassignment`, {
        newAmbassadorId: params.newAmbassadorId,
        reason: params.reason,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ReassignDeliveryError(message, apiError);
    }
  }

  async reschedule(params: RescheduleParams): Promise<void> {
    try {
      await this.apiClient.post(`v1/requests/${params.requestId}/reschedule`, {
        deliveryDate: params.deliveryDate,
        deliveryShift: params.deliveryShift,
        reason: params.reason,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new RescheduleDeliveryError(message, apiError);
    }
  }
}
